import { Input, InputProps } from '@components/ui/input';
import { cn } from '@lib/utils';
import { useStore } from '@tanstack/react-form';
import { useFieldContext, useFormContext } from './form';
import { FormDescription, FormErrorMessage, FormField, FormLabel } from './shared.component';

type TextFieldProps = InputProps & {
  className?: string;
  description?: string;
  inputClassName?: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  tooltip?: React.ReactNode;
};

/**
 * A wrapper component over the basic Input that adds TanStack Form logic and validation.
 * Provides form field context, error handling, and accessibility features.
 * @param props - All other standard HTML input props
 * @returns A form-integrated text field component
 */
export function TextField(props: TextFieldProps) {
  const field = useFieldContext<string | number | undefined | null>();
  const form = useFormContext();
  const {
    className,
    description,
    inputClassName,
    label,
    labelSuffix,
    placeholder,
    required,
    size,
    tooltip,
    type = 'text',
    onChange,
    onKeyDown,
    onPaste,
    min,
    step,
    ...otherProps
  } = props;

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  const isNumberType = type === 'number';

  const minValue = typeof min === 'number' ? min : typeof min === 'string' ? Number(min) : undefined;
  const allowNegative = !(typeof minValue === 'number' && !Number.isNaN(minValue) && minValue >= 0);

  const allowDecimals = isNumberType
    ? (() => {
        if (step === undefined) return true;
        if (typeof step === 'string') {
          if (step.toLowerCase() === 'any') return true;
          const numericStep = Number(step);
          if (!Number.isFinite(numericStep)) return true;
          return !Number.isInteger(numericStep);
        }
        return !Number.isInteger(step);
      })()
    : false;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) return onChange(e);

    const value = e.target.value;

    if (value) {
      field.handleChange(isNumberType ? Number(value) : value);
    } else {
      field.handleChange(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) onKeyDown(e);
    if (!isNumberType) return;

    const key = e.key;
    const isCtrlCmd = e.ctrlKey || e.metaKey;
    const navigationKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight'];
    if (navigationKeys.includes(key) || isCtrlCmd) return;

    if (key === 'e' || key === 'E' || key === '+') {
      e.preventDefault();
      return;
    }
    if (!allowDecimals && (key === '.' || key === ',')) {
      e.preventDefault();
      return;
    }
    if (!allowNegative && key === '-') {
      e.preventDefault();
      return;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (onPaste) onPaste(e);
    if (!isNumberType) return;
    const text = e.clipboardData.getData('text');
    const integerPattern = allowNegative ? /^-?\d*$/ : /^\d*$/;
    const decimalPattern = allowNegative ? /^-?\d*(?:[.,]\d*)?$/ : /^\d*(?:[.,]\d*)?$/;
    const pattern = allowDecimals ? decimalPattern : integerPattern;
    if (!pattern.test(text)) {
      e.preventDefault();
    }
  };

  return (
    <FormField className={cn('w-full', className)}>
      {label && (
        <FormLabel
          htmlFor={field.name}
          labelSuffix={labelSuffix}
          required={required}
          tooltip={tooltip}
        >
          {label}
        </FormLabel>
      )}
      {description && <FormDescription>{description}</FormDescription>}
      <Input
        aria-invalid={!field.state.meta.isValid}
        className={cn('w-full', inputClassName)}
        disabled={isSubmitting}
        id={field.name}
        inputMode={isNumberType ? 'numeric' : undefined}
        name={field.name}
        onBlur={field.handleBlur}
        hasError={!field.state.meta.isValid}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder}
        type={type}
        size={size}
        value={field.state.value ?? ''}
        {...otherProps}
      />
      <FormErrorMessage />
    </FormField>
  );
}
